// Settings = General (app-wide hub) + one page per deity. Each deity row reads
// "NAME — Word" and opens every setting that deity owns, consolidated on one
// page (e.g. TKNE holds both the ORDER templates and the project forms/labels).
// The old first-run Spine editor and the empty Dash page are gone.
import React, { useEffect, useRef, useState } from 'react';
import { getRevealEnabled, setRevealEnabled } from './revealPref';
import { useThemeMode, useThemePref, type ThemePref } from '../v10/theme';
import { Alert, Image, Modal, Platform, Pressable, ScrollView, StyleSheet, Text, View, useWindowDimensions } from 'react-native';
import { ThemedSwitch } from './settings/ThemedSwitch';
import { useTheme, makeThemedStyles, DISPLAY_FONT } from '../v10/theme';
import { restartOnboarding } from '../v10/onboarding';
import { Atmos, Chip, GlassCard, SectionTitle, Field, GoldButton } from '../v10/ui';
import { useAuth } from '../auth/AuthProvider';
import { apiFetch } from '../lib/api';
import { supabase } from '../lib/supabase';
import { scheduleTestReminder } from '../lib/notifications';
import { useSidebarConfig } from './useSidebarConfig';
import { SEATS, SEAT_BY_ID, GEAR_LABEL, DEITY_WORD, portraitFor, type Seat } from '../v10/roster';
import { TIER_LABEL } from './ascension';
import { HrmezSettingsModal } from '../tools/HrmezTool';
import GroupsEditor from './settings/GroupsEditor';
import ValuesEditor from './settings/ValuesEditor';
import EtcIntervalsEditor from './settings/EtcIntervalsEditor';
import CalendarEditor from './settings/CalendarEditor';
import JournalTypesEditor from './settings/JournalTypesEditor';
import ProjectsEditor from './settings/ProjectsEditor';
import TkneEditor from './settings/TkneEditor';
import PomodoroEditor from './settings/PomodoroEditor';
import MetisEditor from './settings/MetisEditor';
import ArgosEditor from './settings/ArgosEditor';
import HrmezEditor from './settings/HrmezEditor';
import OracleEditor from './settings/OracleEditor';
import { GameSettingsEditor } from './settings/GameSettingsEditor';
import SyqiDashboardEditor from './settings/SyqiDashboardEditor';
import DriftRulesEditor from './settings/DriftRulesEditor';
import TrashEditor from './settings/TrashEditor';
import { CouncilEditor } from './settings/CouncilEditor';
import { QuickLinksEditor } from './settings/QuickLinksEditor';
import { onCouncil } from '../live/appActions';
import { ScreenHelp } from '../v10/ScreenHelp';
import { settingsHelp } from './help/settingsHelp';

interface GeneralPage {
  id: string;
  title: string;
  description: string;
}

const GENERAL_PAGES: GeneralPage[] = [
  { id: 'app', title: 'App', description: 'Theme, onboarding, quick links, sidebar, week close and notifications.' },
  { id: 'game', title: 'Game', description: 'Session times, week shape, the climb (ladder sizes and thresholds), seat and alignment windows, green bands and your session widgets.' },
  { id: 'syqi', title: 'SyQi', description: 'Choose which tools appear in each ORDER section.' },
  { id: 'council', title: 'Council', description: 'Council mode: who sits, who answers, read-aloud and the hands-free mic.' },
  { id: 'trash', title: 'Trash & Archive', description: 'Restore or permanently delete trashed items.' },
  { id: 'account', title: 'Account', description: 'Change your password, sign out, or delete your account.' },
];

// Page ids: null = the Settings root, 'general' = the General hub,
// 'general:<id>' = a General sub-page, 'deity:<seat id>' = a deity's page.
type PageId = string | null;

function pageTitle(page: PageId): string {
  if (!page) return 'Settings';
  if (page === 'general') return 'General';
  if (page.startsWith('general:')) return GENERAL_PAGES.find((g) => `general:${g.id}` === page)?.title ?? 'General';
  return SEAT_BY_ID[page.slice('deity:'.length)]?.name ?? 'Settings';
}

export function SettingsSheet({ open, onClose, userId }: { open: boolean; onClose: () => void; userId: string | undefined }) {
  const styles = useStyles();
  const { height: winH } = useWindowDimensions();
  const [page, setPage] = useState<PageId>(null);

  const handleClose = () => {
    setPage(null);
    onClose();
  };
  // "Open the Council" (General → Council): the sheet is a native Modal, so it must close for the room to show.
  const closeRef = useRef(handleClose);
  closeRef.current = handleClose;
  useEffect(() => onCouncil((r) => r && open && closeRef.current()), [open]);

  const parent: PageId = page?.startsWith('general:') ? 'general' : null;
  const seat = page?.startsWith('deity:') ? SEAT_BY_ID[page.slice('deity:'.length)] : undefined;

  // Web: RN-web's slide animation is CSS-keyframe driven and can strand the
  // portal at translateY(100%) (dim scrim, no sheet). No transform on web.
  return (
    <Modal visible={open} transparent animationType={Platform.OS === 'web' ? 'none' : 'slide'} onRequestClose={handleClose}>
      <View style={styles.backdrop}>
        <Atmos style={[styles.sheet, { height: Math.round(winH * 0.88) }]}>
          <View style={styles.header}>
            {page ? (
              <Pressable onPress={() => setPage(parent)} style={styles.back}>
                <Text style={styles.backText}>‹ {parent ? 'General' : 'Settings'}</Text>
              </Pressable>
            ) : null}
            <Text style={styles.title}>{pageTitle(page)}</Text>
            <ScreenHelp help={settingsHelp} style={styles.help} />
            <Pressable onPress={handleClose} style={styles.close}>
              <Text style={styles.closeText}>✕</Text>
            </Pressable>
          </View>

          {!page ? (
            <SettingsRoot onOpen={setPage} />
          ) : page === 'general' ? (
            <ScrollView contentContainerStyle={styles.content}>
              {GENERAL_PAGES.map((g) => (
                <Pressable key={g.id} onPress={() => setPage(`general:${g.id}`)}>
                  <GlassCard style={styles.categoryRow}>
                    <View style={{ flex: 1 }}>
                      <Text style={styles.categoryTitle}>{g.title}</Text>
                      <Text style={styles.categoryDesc}>{g.description}</Text>
                    </View>
                    <Text style={styles.categoryChevron}>›</Text>
                  </GlassCard>
                </Pressable>
              ))}
            </ScrollView>
          ) : page.startsWith('general:') ? (
            <GeneralDetail id={page.slice('general:'.length)} userId={userId} />
          ) : seat ? (
            <DeitySettings seat={seat} userId={userId} />
          ) : null}
        </Atmos>
      </View>
    </Modal>
  );
}

function SettingsRoot({ onOpen }: { onOpen: (page: PageId) => void }) {
  const styles = useStyles();
  return (
    <ScrollView contentContainerStyle={styles.content}>
      <Pressable onPress={() => onOpen('general')}>
        <GlassCard style={styles.categoryRow}>
          <View style={{ flex: 1 }}>
            <Text style={styles.categoryTitle}>General</Text>
            <Text style={styles.categoryDesc}>App, Game, SyQi, Council, Trash & Archive, Account.</Text>
          </View>
          <Text style={styles.categoryChevron}>›</Text>
        </GlassCard>
      </Pressable>

      <SectionTitle style={styles.sectionGap}>Deities</SectionTitle>
      {SEATS.map((s) => {
        const portrait = portraitFor(s.id);
        return (
          <Pressable key={s.id} onPress={() => onOpen(`deity:${s.id}`)}>
            <GlassCard style={styles.deityRow}>
              {portrait ? <Image source={portrait} style={styles.deityThumb} /> : null}
              <Text style={styles.deityName}>{s.name}</Text>
              <Text style={styles.deityWord}>— {DEITY_WORD[s.id]}</Text>
              <View style={{ flex: 1 }} />
              <Text style={styles.categoryChevron}>›</Text>
            </GlassCard>
          </Pressable>
        );
      })}
    </ScrollView>
  );
}

function GeneralDetail({ id, userId }: { id: string; userId: string | undefined }) {
  const styles = useStyles();
  switch (id) {
    case 'app':
      return <GeneralSettings userId={userId} />;
    case 'game':
      return <GameSettingsEditor userId={userId} />;
    case 'syqi':
      return <SyqiDashboardEditor userId={userId} />;
    case 'council':
      return <CouncilEditor userId={userId} />;
    case 'trash':
      return (
        <ScrollView contentContainerStyle={styles.content}>
          <TrashEditor userId={userId} />
        </ScrollView>
      );
    case 'account':
      return <AccountSettings />;
    default:
      return null;
  }
}

/** Every setting a deity owns, on one page. Deities with nothing to configure say so. */
function DeitySettings({ seat, userId }: { seat: Seat; userId: string | undefined }) {
  const styles = useStyles();
  const [hrmezPanel, setHrmezPanel] = useState(false);
  const portrait = portraitFor(seat.id);

  let body: React.ReactNode;
  switch (seat.id) {
    case 'astra':
      body = (
        <>
          <OracleEditor userId={userId} />
          <SectionTitle style={styles.sectionGap}>Drift rules</SectionTitle>
          <DriftRulesEditor userId={userId} />
        </>
      );
      break;
    case 'hrmez':
      body = (
        <>
          <HrmezEditor userId={userId} />
          <SectionTitle style={styles.sectionGap}>Folders, routing & Google Drive</SectionTitle>
          <GoldButton ghost label="Open folders, routing rules & Drive" onPress={() => setHrmezPanel(true)} style={styles.actionGap} />
          {userId ? <HrmezSettingsModal visible={hrmezPanel} userId={userId} onClose={() => setHrmezPanel(false)} /> : null}
        </>
      );
      break;
    case 'kyrs':
      body = (
        <>
          <SectionTitle style={styles.sectionGap}>Groups</SectionTitle>
          <GroupsEditor userId={userId} />
          <SectionTitle style={styles.sectionGap}>Values</SectionTitle>
          <ValuesEditor userId={userId} />
          <SectionTitle style={styles.sectionGap}>ETC Intervals</SectionTitle>
          <EtcIntervalsEditor userId={userId} />
        </>
      );
      break;
    case 'krnos':
      body = <CalendarEditor userId={userId} />;
      break;
    case 'tkne':
      body = (
        <>
          <TkneEditor userId={userId} />
          <ProjectsEditor userId={userId} />
        </>
      );
      break;
    case 'metis':
      body = (
        <>
          <MetisEditor userId={userId} />
          <SectionTitle style={styles.sectionGap}>Pomodoro</SectionTitle>
          <PomodoroEditor userId={userId} />
        </>
      );
      break;
    case 'clio':
      body = <JournalTypesEditor userId={userId} />;
      break;
    case 'argos':
      body = <ArgosEditor userId={userId} />;
      break;
    default:
      body = <Text style={styles.hint}>{seat.name} has nothing to set up. Everything happens in the tool.</Text>;
  }

  return (
    <ScrollView contentContainerStyle={styles.content}>
      <View style={styles.deityHero}>
        {portrait ? <Image source={portrait} style={styles.heroThumb} /> : null}
        <View style={{ flex: 1 }}>
          <Text style={styles.heroWord}>{DEITY_WORD[seat.id]}</Text>
          <Text style={styles.categoryDesc}>
            {GEAR_LABEL[seat.gear].name} · {TIER_LABEL[seat.tier]} — {seat.ability}.
          </Text>
        </View>
      </View>
      {body}
    </ScrollView>
  );
}

function GeneralSettings({ userId }: { userId: string | undefined }) {
  const styles = useStyles();
  const { c } = useTheme();
  const { items: sidebarItems, setVisible, move } = useSidebarConfig(userId);
  const [notifStatus, setNotifStatus] = useState<string | null>(null);
  const [revealOn, setRevealOn] = useState(false);
  const [themeMode] = useThemeMode();
  const [themePref, setThemePref] = useThemePref();
  const [replayed, setReplayed] = useState(false);

  useEffect(() => {
    if (userId) void getRevealEnabled(userId).then(setRevealOn);
  }, [userId]);

  const testNotification = async () => {
    const result = await scheduleTestReminder();
    setNotifStatus(result);
  };

  return (
    <ScrollView contentContainerStyle={styles.content}>
      <SectionTitle style={styles.sectionGap}>Appearance</SectionTitle>
      <View style={styles.sidebarRow}>
        <Text style={styles.sidebarLabel}>Theme · now {themeMode === 'dark' ? 'Night' : 'Day'}</Text>
        <View style={{ flexDirection: 'row', gap: 6 }}>
          {(['dark', 'light', 'live'] as ThemePref[]).map((p) => (
            <Chip key={p} small label={p === 'dark' ? 'Night' : p === 'light' ? 'Day' : 'Live'} active={themePref === p} onPress={() => setThemePref(p)} />
          ))}
        </View>
      </View>

      <SectionTitle style={styles.sectionGap}>Onboarding</SectionTitle>
      <View style={styles.sidebarRow}>
        <Text style={styles.sidebarLabel}>{replayed ? 'Restarted. Close Settings to begin.' : 'SyQi guided setup'}</Text>
        <Chip
          small
          label="Replay onboarding"
          active={replayed}
          onPress={() => {
            restartOnboarding();
            setReplayed(true);
          }}
        />
      </View>

      <SectionTitle style={styles.sectionGap}>Quick links</SectionTitle>
      <QuickLinksEditor />

      <SectionTitle style={styles.sectionGap}>Sidebar</SectionTitle>
      <Text style={styles.hint}>Controls what shows in the menu drawer (top-left). Same list as web.</Text>
      {sidebarItems.length === 0 ? (
        <Text style={styles.hint}>Nothing configured yet.</Text>
      ) : (
        sidebarItems.map((item, idx) => (
          <View key={item.id} style={styles.sidebarRow}>
            <View style={styles.sidebarReorder}>
              <Pressable
                onPress={() => move(item.id, -1)}
                disabled={idx === 0}
                style={[styles.reorderBtn, idx === 0 && styles.reorderBtnDisabled]}
              >
                <Text style={styles.reorderText}>▲</Text>
              </Pressable>
              <Pressable
                onPress={() => move(item.id, 1)}
                disabled={idx === sidebarItems.length - 1}
                style={[styles.reorderBtn, idx === sidebarItems.length - 1 && styles.reorderBtnDisabled]}
              >
                <Text style={styles.reorderText}>▼</Text>
              </Pressable>
            </View>
            <Text style={styles.sidebarLabel}>{item.label}</Text>
            <ThemedSwitch
              value={item.visible}
              onValueChange={(v) => setVisible(item.id, v)}
              disabled={item.href === '/' || item.href === '/settings'}
            />
          </View>
        ))
      )}

      <SectionTitle style={styles.sectionGap}>Sunday-evening week close</SectionTitle>
      <Text style={styles.hint}>The evening session on your week boundary day closes the week. This also shows any week you missed when you open the app.</Text>
      <View style={styles.sidebarRow}>
        <Text style={styles.sidebarLabel}>Show a missed week close on open</Text>
        <ThemedSwitch
          value={revealOn}
          onValueChange={(v) => {
            setRevealOn(v);
            if (userId) void setRevealEnabled(userId, v);
          }}
        />
      </View>

      <SectionTitle style={styles.sectionGap}>Notifications</SectionTitle>
      <GoldButton ghost label="Send test reminder (fires in 10s)" onPress={() => void testNotification()} style={styles.actionGap} />
      {notifStatus ? <Text style={styles.hint}>{notifStatus}</Text> : null}
    </ScrollView>
  );
}

function AccountSettings() {
  const styles = useStyles();
  const { session, signOut } = useAuth();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [saving, setSaving] = useState(false);

  const handleChangePassword = async () => {
    if (!newPassword || newPassword !== confirmPassword) {
      Alert.alert('Passwords must match', 'Enter the same new password in both fields.');
      return;
    }
    setSaving(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) throw error;
      setNewPassword('');
      setConfirmPassword('');
      Alert.alert('Password updated');
    } catch (e) {
      Alert.alert('Failed to update password', e instanceof Error ? e.message : 'Something went wrong.');
    } finally {
      setSaving(false);
    }
  };

  const confirmDeleteAccount = () => {
    Alert.alert('Delete account?', 'This permanently deletes your account and data. This cannot be undone.', [
      { text: 'Cancel', style: 'cancel' },
      {
        text: 'Delete forever',
        style: 'destructive',
        onPress: async () => {
          try {
            await apiFetch('/api/mobile/delete-account', {
              method: 'POST',
              body: JSON.stringify({ confirm: 'DELETE' }),
            });
            await signOut();
          } catch (e) {
            Alert.alert('Deletion failed', e instanceof Error ? e.message : 'Try again or contact support.');
          }
        },
      },
    ]);
  };

  return (
    <ScrollView contentContainerStyle={styles.content}>
      <SectionTitle style={styles.sectionGap}>Change Password</SectionTitle>
      <Text style={styles.hint}>Update the password for {session?.user.email}.</Text>
      <Field
        style={styles.fieldGap}
        placeholder="New password"
        secureTextEntry
        value={newPassword}
        onChangeText={setNewPassword}
      />
      <Field
        style={styles.fieldGap}
        placeholder="Confirm new password"
        secureTextEntry
        value={confirmPassword}
        onChangeText={setConfirmPassword}
      />
      <GoldButton
        label={saving ? 'Updating…' : 'Update Password'}
        onPress={() => {
          if (saving || !newPassword || !confirmPassword) return;
          void handleChangePassword();
        }}
        style={[styles.actionGap, (saving || !newPassword || !confirmPassword) && styles.actionButtonDisabled]}
      />

      <SectionTitle style={styles.sectionGap}>Account</SectionTitle>
      <GoldButton ghost label="Sign out" onPress={() => void signOut()} style={styles.actionGap} />
      <Pressable style={styles.dangerButton} onPress={confirmDeleteAccount}>
        <Text style={styles.dangerText}>Delete account</Text>
      </Pressable>
      <Text style={styles.hint}>
        Deleting your account permanently removes your login and data. This cannot be undone.
      </Text>
    </ScrollView>
  );
}

const useStyles = makeThemedStyles(({ t, c, radii }) =>
  StyleSheet.create({
  sectionGap: { marginTop: 18 },
  backdrop: { flex: 1, backgroundColor: c.overlay, justifyContent: 'flex-end' },
  sheet: {
    // NOT `flex: 0`: RN-web expands it to `0 1 0%`, whose 0% basis beats the
    // explicit height and collapses the sheet to 1px (the dim-only-screen bug).
    flexGrow: 0,
    flexShrink: 0,
    flexBasis: 'auto',
    height: '88%',
    borderTopLeftRadius: radii.panel,
    borderTopRightRadius: radii.panel,
    borderWidth: 1,
    borderBottomWidth: 0,
    borderColor: t.navyLine,
    overflow: 'hidden',
    boxShadow: `0 -10px 30px ${t.glowSoft}`,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 16,
  },
  title: { fontSize: 18, fontWeight: '900', letterSpacing: 1.5, color: t.ivory, fontFamily: DISPLAY_FONT, textTransform: 'uppercase' },
  back: { position: 'absolute', left: 16, top: 12, padding: 4 },
  backText: { fontSize: 15, fontWeight: '700', color: t.goldBright },
  close: { position: 'absolute', right: 16, top: 14, padding: 4 },
  help: { position: 'absolute', right: 58, top: 14 },
  closeText: { fontSize: 18, color: t.ivorySoft },
  content: { paddingHorizontal: 16, paddingBottom: 48, gap: 8 },

  categoryRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    borderRadius: radii.tile,
    paddingVertical: 14,
    paddingHorizontal: 14,
    marginTop: 8,
  },
  categoryTitle: { fontSize: 15, fontWeight: '800', color: t.ivory, fontFamily: DISPLAY_FONT, textTransform: 'uppercase' },
  categoryDesc: { fontSize: 12, color: t.ivorySoft, marginTop: 2 },
  categoryChevron: { fontSize: 20, fontWeight: '700', color: t.goldBright },

  deityRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    borderRadius: radii.tile,
    paddingVertical: 10,
    paddingHorizontal: 12,
    marginTop: 6,
  },
  deityThumb: { width: 34, height: 34, borderRadius: 17 },
  deityName: { fontSize: 15, fontWeight: '800', color: t.ivory, fontFamily: DISPLAY_FONT, textTransform: 'uppercase' },
  deityWord: { fontSize: 14, fontWeight: '700', color: t.goldBright },
  deityHero: { flexDirection: 'row', alignItems: 'center', gap: 12, marginTop: 4 },
  heroThumb: { width: 56, height: 56, borderRadius: 28 },
  heroWord: { fontSize: 16, fontWeight: '800', color: t.goldBright, fontFamily: DISPLAY_FONT, textTransform: 'uppercase' },

  hint: { fontSize: 12, color: t.ivorySoft, marginTop: 6, fontStyle: 'italic' },
  fieldGap: { marginTop: 10 },
  actionGap: { marginTop: 10 },
  dangerButton: {
    marginTop: 14,
    borderRadius: radii.tile,
    borderWidth: 1,
    borderColor: t.lapsed,
    paddingVertical: 12,
    alignItems: 'center',
  },
  dangerText: { color: t.lapsed, fontWeight: '800' },
  actionButtonDisabled: { opacity: 0.5 },

  sidebarRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    backgroundColor: t.glass,
    borderRadius: radii.tile,
    paddingVertical: 8,
    paddingHorizontal: 10,
    marginTop: 6,
    borderWidth: 1,
    borderColor: t.lineFaint,
    boxShadow: `0 4px 14px ${t.glowSoft}`,
  },
  sidebarReorder: { gap: 2 },
  reorderBtn: {
    width: 26,
    height: 20,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 6,
    backgroundColor: t.lineFaint,
  },
  reorderBtnDisabled: { opacity: 0.25 },
  reorderText: { fontSize: 10, color: t.goldBright, fontWeight: '900' },
  sidebarLabel: { flex: 1, fontSize: 14, fontWeight: '700', color: t.ivory },
}),
);
